/**
 * Shared "draft payments" section used by both AddSegmentDialog and
 * EditSegmentDialog.
 *
 * Contract:
 *   - `pendingPayments`, `setPendingPayments`: array state owned by the
 *     parent. Each entry: { amount, currency, payment_method_id, description,
 *     status, payment_date, notes }.
 *   - `paymentMethods`: result from /payment-methods.
 *   - `segmentCountry`: ISO country (or pretty name) used to bias the
 *     CurrencySelectFiltered dropdown to the local currency.
 *   - `existingPayments` + `renderExistingPayment`: optional render-prop
 *     pair for showing already-saved rows above the drafts (Edit dialog).
 *   - `defaultOpen`: whether the section is expanded on first paint.
 *   - `disabled`: makes inputs read-only (used when parent is mid-save).
 *
 * Field grid lives in `PaymentRowEditor` so the inline quick-add form
 * in ItineraryView and these draft rows stay visually identical.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, Plus, Trash2 } from "lucide-react";
import { getCountryCurrency, getHomeCurrency } from "@/utils/currencies";
import { formatCurrency } from "@/utils/formatters";
import PaymentRowEditor from "@/components/shared/PaymentRowEditor";
import PaymentsSummary from "@/components/shared/PaymentsSummary";

const emptyDraft = (segmentCountry) => ({
  amount: "",
  currency: getCountryCurrency(segmentCountry) || getHomeCurrency() || "USD",
  payment_method_id: "",
  description: "fare",
  status: "paid",
  payment_date: new Date().toISOString().slice(0, 10),
  notes: "",
});

const DraftPaymentsSection = ({
  pendingPayments,
  setPendingPayments,
  paymentMethods = [],
  segmentCountry,
  segmentType,
  existingPayments,
  renderExistingPayment,
  // Optional: when supplied, the collapsed section header shows the
  // shared PaymentsSummary (total + hover tooltip breakdown) instead of
  // a plain "Payments (n)" pill. Edit Segment dialog provides this; Add
  // Segment doesn't (no existing payments yet).
  getPaymentMethodName,
  defaultOpen = false,
  disabled = false,
  title = "Payments",
  // Inline-mode hooks (used by SegmentCard / GroupedFlightCard):
  // - `onAddClick`: when provided, the "+ Add payment" button calls
  //   this instead of pushing a draft into pendingPayments. The parent
  //   then renders its own immediate-save form via `addFormSlot`.
  // - `addFormSlot`: rendered in place of the "+ Add payment" button
  //   when truthy (i.e. user is already adding). Lets SegmentCard
  //   reuse this section's collapsible header + row rendering while
  //   keeping its own per-row save/cancel handlers.
  onAddClick,
  addFormSlot,
  addButtonLabel = "Add payment",
  addButtonTestId = "inline-payment-add-btn",
}) => {
  const hasDrafts = pendingPayments.length > 0;
  const hasExisting = Array.isArray(existingPayments) && existingPayments.length > 0;
  // Default-open when there's already content the user is likely to edit.
  const [open, setOpen] = useState(defaultOpen || hasDrafts || hasExisting);

  const totalCount = pendingPayments.length + (existingPayments?.length || 0);

  const updateAt = (i, patch) => {
    const next = [...pendingPayments];
    next[i] = { ...next[i], ...patch };
    setPendingPayments(next);
  };

  return (
    <div data-testid="draft-payments-section">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-left py-2 px-3 rounded-md hover:bg-sky-50 dark:hover:bg-slate-800 transition-colors"
        data-testid="toggle-payments-section"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200 min-w-0 flex-1">
          {open ? <ChevronDown className="h-4 w-4 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 flex-shrink-0" />}
          {/* When the dialog/inline view passed a method-name resolver,
              surface the shared PaymentsSummary (total + hover-detail).
              PaymentsSummary already renders its own "Payments:" label,
              so we suppress the leading title here to avoid the
              "Payments  $ Payments: $1,184.00 (2)" duplicate the user
              pointed out. */}
          {hasExisting && getPaymentMethodName ? (
            <span onClick={(e) => e.stopPropagation()} className="flex-1 min-w-0">
              <PaymentsSummary
                payments={existingPayments}
                formatCurrency={formatCurrency}
                getPaymentMethodName={getPaymentMethodName}
                testId="draft-payments-existing-summary"
              />
            </span>
          ) : (
            <>
              <span>{title}</span>
              {totalCount > 0 && (
                <span className="text-xs bg-sky-100 text-sky-700 dark:bg-sky-900 dark:text-sky-200 rounded-full px-2 py-0.5">
                  {totalCount}
                </span>
              )}
            </>
          )}
        </span>
        <span className="text-xs text-gray-500 dark:text-slate-400 flex-shrink-0 ml-2">Optional</span>
      </button>

      {open && (
        <div className="mt-2 pl-6 space-y-3">
          {/* Existing payments (Edit dialog only) — rendered via render-prop
              so the parent keeps full control of edit/delete affordances. */}
          {hasExisting && (
            <div className="space-y-2">
              {existingPayments.map((p) => (
                <div key={p.id} data-testid={`existing-payment-${p.id}`}>
                  {renderExistingPayment ? renderExistingPayment(p) : null}
                </div>
              ))}
            </div>
          )}

          {/* Draft rows (dialog mode — multi-add, commit-on-dialog-save) */}
          {pendingPayments.map((p, i) => (
            <div
              key={i}
              className="relative border border-sky-200 dark:border-sky-900/60 rounded-lg p-3 bg-sky-50/40 dark:bg-sky-950/20 space-y-2"
              data-testid={`inline-payment-row-${i}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-sky-700 dark:text-sky-300">
                  New payment #{i + 1}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setPendingPayments(pendingPayments.filter((_, idx) => idx !== i))}
                  disabled={disabled}
                  className="h-6 w-6 p-0 text-gray-500 hover:text-red-600"
                  data-testid={`inline-payment-remove-${i}`}
                  aria-label="Remove payment"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>

              <PaymentRowEditor
                payment={p}
                onChange={(patch) => updateAt(i, patch)}
                paymentMethods={paymentMethods}
                segmentCountry={segmentCountry}
                segmentType={segmentType}
                disabled={disabled}
                testIdPrefix={`inline-payment-${i}`}
              />
            </div>
          ))}

          {/* Inline-mode add form (immediate-save context) replaces the
              "+ Add payment" button while it's active. */}
          {addFormSlot}

          {!addFormSlot && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                if (onAddClick) {
                  onAddClick();
                } else {
                  setPendingPayments([...pendingPayments, emptyDraft(segmentCountry)]);
                }
              }}
              disabled={disabled}
              className="w-full"
              data-testid={addButtonTestId}
            >
              <Plus className="h-4 w-4 mr-1" />
              {addButtonLabel}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default DraftPaymentsSection;
